import { Link } from '@tanstack/react-router'
import { DetailCard } from '@trellis/roots/data'
import { TagSelector } from '@trellis/roots/tags'
import type { TagResponseDto } from '@trellis/shared'
import { Tags } from 'lucide-react'
import { toast } from 'sonner'

interface TagsEditorCardProps {
  tags: TagResponseDto[]
  allTags: TagResponseDto[]
  onAssign: (tagIds: string[]) => Promise<unknown>
  onRemove: (tagId: string) => Promise<unknown>
  onCreateTag: (
    name: string,
    color?: string,
  ) => Promise<{ id: string } | null | undefined>
}

export function TagsEditorCard({
  tags,
  allTags,
  onAssign,
  onRemove,
  onCreateTag,
}: TagsEditorCardProps) {
  return (
    <DetailCard
      title="Tags"
      icon={Tags}
      headerAction={
        <Link
          to="/settings/tags"
          className="text-muted-foreground hover:text-foreground text-xs transition-colors"
        >
          Manage →
        </Link>
      }
    >
      <TagSelector
        tags={allTags}
        value={tags.map((tag) => tag.id)}
        onChange={(newSelectedIds) => {
          const oldIds = tags.map((tag) => tag.id)
          const added = newSelectedIds.filter((tagId) => !oldIds.includes(tagId))
          const removed = oldIds.filter((tagId) => !newSelectedIds.includes(tagId))
          if (added.length > 0) {
            void onAssign(added).catch(() => toast.error('Failed to assign tag'))
          }
          for (const tagId of removed) {
            void onRemove(tagId).catch(() => toast.error('Failed to remove tag'))
          }
        }}
        onCreateTag={async (name, color) => {
          try {
            const created = await onCreateTag(name, color)
            if (created) {
              void onAssign([created.id]).catch(() => toast.error('Failed to assign tag'))
            }
          } catch {
            toast.error('Failed to create tag')
          }
        }}
      />
    </DetailCard>
  )
}
